import { useEffect, useState, useCallback } from "react";
import { lineItemsApi, usersApi, brandsApi, adunitPositionSlotStatusApi } from "./services/makerCheckerApi";
import Table from "./components/Table";
import PayloadModal, { ApproveRejectModal } from "./PayloadModal/PayloadModal";
import BrandChecker from "./BrandChecker";
import { Toast } from "../../shared/Toast";
import { roles } from "../../utils/constants";
import LineitemPreview from "../campaign/LineitemPreview";
import { SlDrawer } from '@shoelace-style/shoelace/dist/react';

const MakerChecker = ({ type: propType }) => {
  const userRole = localStorage.getItem("role");

  const getAvailableTypes = useCallback(() => {
    if (userRole === roles.ops) {
      return ["lineItems", "adunitPositionSlotStatus"];
    } else if (userRole === roles.b2b) {
      return ["users", "brands"];
    }
    return ["lineItems"];
  }, [userRole]);

  const availableTypes = getAvailableTypes();

  const getInitialType = useCallback(() => {
    if (propType) return propType;
    if (userRole === roles.ops) return "lineItems";
    if (userRole === roles.b2b) return "users";
    return "lineItems";
  }, [propType, userRole]);

  const [type, setType] = useState(() => getInitialType());
  const [mainTab, setMainTab] = useState("maker");
  const [subTab, setSubTab] = useState("pending");
  const [data, setData] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedPayload, setSelectedPayload] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedUpdatedPayload, setSelectedUpdatedPayload] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBrandData, setSelectedBrandData] = useState(null);
  const [isBrandCheckerOpen, setIsBrandCheckerOpen] = useState(false);
  const [activeRole, setActiveRole] = useState(null);
  const [loading, setLoading] = useState(false);

  const mainTabs = ["maker", "checker"];
  const subTabs = ["pending", "approved", "rejected"];

  useEffect(() => {
    if (!propType) {
      const validTypes = getAvailableTypes();
      if (!validTypes.includes(type)) {
        setType(validTypes[0]);
      }
    }
  }, [userRole, type, getAvailableTypes, propType]);

  useEffect(() => {
    setMainTab("maker");
    setSubTab("pending");
    setCurrentPage(0);
    setData([]);
    setCounts({ pending: 0, approved: 0, rejected: 0 });
  }, [type]);

  const getApiService = useCallback(() => {
    switch (type) {
      case "lineItems":
        return lineItemsApi;
      case "users":
        return usersApi;
      case "brands":
        return brandsApi;
      case "adunitPositionSlotStatus":
        return adunitPositionSlotStatusApi;
      default:
        return lineItemsApi;
    }
  }, [type]);

  useEffect(() => {
    if (type === "brands") {
      const mcRole = Number(localStorage.getItem("mc_role"));
      if (mcRole === 3) {
        setActiveRole("checker");
      } else if (mcRole === 1) {
        setActiveRole("maker");
      } else if (mcRole === 2) {
        setActiveRole("checker");
      }
    }
  }, [type]);

  useEffect(() => {
    const fetchCounts = async () => {
      const apiService = getApiService();
      try {
        if (type === "brands") {
          const result = await apiService.getTotalCount(mainTab);
          setCounts({
            pending: result.pending || 0,
            approved: result.approved || 0,
            rejected: result.rejected || 0,
          });
        } else {
          const result = await apiService.getTotalCount(mainTab);
          setCounts({
            pending: result.pending || 0,
            approved: result.approved || 0,
            rejected: result.rejected || 0,
          });
        }
      } catch (error) {
        console.error("Error fetching counts:", error);
      }
    };

    if (type === "brands" && mainTab) {
      fetchCounts();
    } else if (type !== "brands") {
      fetchCounts();
    }
  }, [mainTab, type, getApiService]);

  const fetchData = async () => {
    setLoading(true);
    const apiService = getApiService();
    try {
      let result = [];

      if (type === "brands") {
        if (!mainTab) {
          setLoading(false);
          return;
        }
        if (subTab === "pending") {
          result = await apiService.getPending(currentPage, mainTab);
        } else if (subTab === "approved") {
          result = await apiService.getApproved(currentPage, mainTab);
        } else if (subTab === "rejected") {
          result = await apiService.getRejected(currentPage, mainTab);
        }
      } else {
        if (subTab === "pending") {
          result = await apiService.getPending(currentPage, mainTab);
        } else if (subTab === "approved") {
          result = await apiService.getApproved(currentPage, mainTab);
        } else if (subTab === "rejected") {
          result = await apiService.getRejected(currentPage, mainTab);
        }
      }
      console.log(result);
      setData(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {

    if (type === "brands") {
      if (mainTab) {
        fetchData();
      }
    } else {
      fetchData();
    }
  }, [mainTab, subTab, currentPage, type, getApiService]);

  const handleViewPayload = (item) => {
    setSelectedRow(item);
    if (type === "brands") {
      // For brands, use BrandChecker
      setSelectedBrandData(item);
      setIsBrandCheckerOpen(true);
    } else {
      // For lineItems and users, use PayloadModal
      setSelectedPayload(item.payload);
      setSelectedUpdatedPayload(item.updated_payload);
      setIsModalOpen(true);
    }
  };

  const handleApprove = async (item) => {
    const apiService = getApiService();
    try {
      let result;

      if (type === "brands") {
        result = await apiService.approve(item.mc_id, item.route);
        if (result.code === 200) {
          Toast("success", "Approved Successfully");
          setIsBrandCheckerOpen(false);
          setSelectedBrandData(null);
          const countsResult = await apiService.getTotalCount(mainTab);
          setCounts({
            pending: countsResult.pending || 0,
            approved: countsResult.approved || 0,
            rejected: countsResult.rejected || 0,
          });
          if (subTab === "pending") {
            const data = await apiService.getPending(currentPage, mainTab);
            setData(Array.isArray(data) ? data : []);
          } else if (subTab === "approved") {
            const data = await apiService.getApproved(currentPage, mainTab);
            setData(Array.isArray(data) ? data : []);
          } else if (subTab === "rejected") {
            const data = await apiService.getRejected(currentPage, mainTab);
            setData(Array.isArray(data) ? data : []);
          }
        } else {
          Toast("error", result?.data?.message || "Failed to approve");
        }
      } else {
        result = await apiService.approve(item.mc_id);
        if (result.status === "SUCCESS") {
          Toast("success", result.message || "Approved Successfully");
          setIsModalOpen(false);
          const countsResult = await apiService.getTotalCount(mainTab);
          setCounts({
            pending: countsResult.pending || 0,
            approved: countsResult.approved || 0,
            rejected: countsResult.rejected || 0,
          });
          if (subTab === "pending") {
            const data = await apiService.getPending(currentPage, mainTab);
            setData(Array.isArray(data) ? data : []);
          } else if (subTab === "approved") {
            const data = await apiService.getApproved(currentPage, mainTab);
            setData(Array.isArray(data) ? data : []);
          } else if (subTab === "rejected") {
            const data = await apiService.getRejected(currentPage, mainTab);
            setData(Array.isArray(data) ? data : []);
          }
        } else {
          Toast("error", result?.data?.message || "Failed to approve");
        }
      }
    } catch (error) {
      console.error("Error approving:", error);
      Toast("error", "Failed to approve request");
    }
  };

  const handleReject = async (item, reason) => {
    if (!reason) return;

    const apiService = getApiService();
    try {
      let result;

      if (type === "brands") {
        result = await apiService.reject(item.mc_id, reason, item.route);
        if (result.code === 200) {
          Toast("success", "Rejected Successfully");
          setIsBrandCheckerOpen(false);
          setSelectedBrandData(null);
          const countsResult = await apiService.getTotalCount(mainTab);
          setCounts({
            pending: countsResult.pending || 0,
            approved: countsResult.approved || 0,
            rejected: countsResult.rejected || 0,
          });
          if (subTab === "pending") {
            const data = await apiService.getPending(currentPage, mainTab);
            setData(Array.isArray(data) ? data : []);
          } else if (subTab === "approved") {
            const data = await apiService.getApproved(currentPage, mainTab);
            setData(Array.isArray(data) ? data : []);
          } else if (subTab === "rejected") {
            const data = await apiService.getRejected(currentPage, mainTab);
            setData(Array.isArray(data) ? data : []);
          }
        } else {
          Toast("error", result?.data?.message || "Failed to reject");
        }
      } else {
        result = await apiService.reject(item.mc_id, reason);
        if (result.status === "SUCCESS") {
          Toast("info", `Request rejected with reason: ${reason}`);
          setIsModalOpen(false);
          const countsResult = await apiService.getTotalCount(mainTab);
          setCounts({
            pending: countsResult.pending || 0,
            approved: countsResult.approved || 0,
            rejected: countsResult.rejected || 0,
          });
          if (subTab === "pending") {
            const data = await apiService.getPending(currentPage, mainTab);
            setData(Array.isArray(data) ? data : []);
          } else if (subTab === "approved") {
            const data = await apiService.getApproved(currentPage, mainTab);
            setData(Array.isArray(data) ? data : []);
          } else if (subTab === "rejected") {
            const data = await apiService.getRejected(currentPage, mainTab);
            setData(Array.isArray(data) ? data : []);
          }
        } else {
          Toast("error", result?.data?.message || "Failed to reject");
        }
      }
    } catch (error) {
      console.error("Error rejecting:", error);
      Toast("error", "Failed to reject request");
    }
  };

  const handleRejectWithPrompt = async (item) => {
    const reason = prompt("Enter reject reason:");
    if (!reason) return;
    await handleReject(item, reason);
  };

  const handlePageChange = (event) => {
    setCurrentPage(event.selected);
  };

  const handleMainTabChange = (tab) => {
    setMainTab(tab);
    setSubTab("pending");
    setCurrentPage(0);
  };

  const handleSubTabChange = (tab) => {
    setSubTab(tab);
    setCurrentPage(0);
  };

  const handleTypeChange = (newType) => {
    setType(newType);
    setCurrentPage(0);
    setSubTab("pending");
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPayload(null);
    setSelectedUpdatedPayload(null);
    setSelectedRow(null);
    fetchData();
  };
  const currentType =  mainTab

  return (
    <div className="w-full mt-4 md:mt-10 p-2 md:p-4">
      {availableTypes.length > 1 && (
        <div className="flex items-center flex-wrap gap-2 md:gap-4 mb-3 md:mb-6 p-2.5 md:p-4 bg-gray-50 rounded-lg border border-gray-200">
          <label className="font-semibold text-gray-700 text-sm md:text-base">Select Type:</label>
          <div className="flex gap-2 flex-wrap">
            {availableTypes.map((availableType) => (
              <button
                key={availableType}
                onClick={() => handleTypeChange(availableType)}
                className={`px-3 py-1.5 md:px-4 md:py-2 text-sm rounded-md font-medium transition-colors ${type === availableType ? "bg-blue-600 text-white shadow-md" : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"}`}
              >
                {availableType === "lineItems" ? "Line Items" : availableType === "users" ? "Users" : availableType === "brands" ? "Brands" : availableType === "adunitPositionSlotStatus" ? "Adunit Position Slot Status" : availableType}
              </button>
            ))}
          </div>
        </div>
      )}
        <div className="flex flex-wrap border-b border-gray-300 mb-3 md:mb-6">
          {mainTabs.map((tab) => (
            <button key={tab} onClick={() => handleMainTabChange(tab)} className={`px-3 md:px-4 py-2 -mb-px font-medium text-sm rounded-t whitespace-nowrap ${mainTab === tab ? "border-b-2 bg-blue-300" : "text-gray-500 hover:bg-blue-200"}`}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

      <div className="flex flex-wrap border-b border-gray-300 mb-3 md:mb-6">
        {subTabs.map((tab) => (
          <button key={tab} onClick={() => handleSubTabChange(tab)} className={`px-3 md:px-4 py-2 -mb-px font-medium text-sm rounded-t whitespace-nowrap inline-flex items-center gap-1.5 ${subTab === tab ? "border-b-2 bg-blue-300" : "text-gray-500 hover:bg-blue-200"}`}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)} <span className="px-2 py-0.5 rounded-full bg-white text-blue-800 text-xs font-semibold">{counts[tab] || 0}</span>
          </button>
        ))}
      </div>

        <div className="bg-white shadow rounded p-2 md:p-4">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
           <Table 
             data={data} 
             currentPage={currentPage} 
             status={subTab} 
             type={currentType} 
             onViewPayload={handleViewPayload} 
             onApprove={type === "brands" ? undefined : handleApprove} 
             onReject={type === "brands" ? undefined : handleRejectWithPrompt} 
             onPageChange={handlePageChange} 
             total={counts[subTab] || 0} 
             viewButton={type === "adunitPositionSlotStatus" ? false : true}
           />
          )}
        </div>
      {isModalOpen && type === "lineItems" && selectedRow?.ref_table_id && (
        <SlDrawer
              open={isModalOpen}
              onSlAfterHide={() => { setIsModalOpen(false) }}
              style={{ '--size': '1280px' }}>
              <div className="flex flex-col gap-4">
                <ApproveRejectModal
                  mcAction={selectedRow}
                  onApprove={() => {
                    setIsModalOpen(false);
                    fetchData();
                  }}
                  onReject={() => {
                    setIsModalOpen(false);
                    fetchData();
                  }}
                  /* Same rule BrandChecker and the PayloadModal path use: a maker cannot
                     action their own request. Only readOnly is passed here -- this drawer's
                     built-in handler already dispatches to lineItemsApi correctly, and
                     supplying performApprove would bypass the onApprove/onReject callbacks
                     above, leaving the drawer open after an action. */
                  readOnly={
                    subTab !== "pending" ||
                    localStorage.getItem('mc_role') === '1' ||
                    (activeRole === 'maker' && localStorage.getItem('mc_role') === '3')
                  }
                />
                <LineitemPreview campaign_id={selectedRow?.ref_table_id} />
              </div>
          </SlDrawer>
      )}
       {/* PayloadModal derives the comparison from the row itself (it needs
           payload_version/request_payload/payload_difference to pick the renderer). */}
       {isModalOpen && type !== "brands" && type!=="lineItems" && (
         <PayloadModal
           mcAction={selectedRow}
           isOpen={isModalOpen}
           onClose={handleCloseModal}
           /* Without these the modal falls back to its own handlers, whose dispatch is
              `ref_table_name === 'lineitems' ? lineItemsApi : usersApi` -- so an adunit row
              went to approveUserRequest, which handles only the 9 user tables and has no
              else branch: nothing was applied, yet it reported success. handleApprove
              dispatches per tab via getApiService(), so it is passed in. */
           performApprove={handleApprove}
           performReject={handleRejectWithPrompt}
           /* Same rule BrandChecker already uses: a maker cannot action their own request. */
           readOnly={
             subTab !== "pending" ||
             localStorage.getItem('mc_role') === '1' ||
             (activeRole === 'maker' && localStorage.getItem('mc_role') === '3')
           }
         />
       )}

       {isBrandCheckerOpen && type === "brands" && selectedBrandData && (
         <BrandChecker
           data={selectedBrandData}
           onApprove={() => handleApprove(selectedBrandData)}
           onReject={(reason) => handleReject(selectedBrandData, reason)}
           onClose={() => {
             setIsBrandCheckerOpen(false);
             setSelectedBrandData(null);
           }}
           isOnlyView={
             subTab !== "pending" ||
             localStorage.getItem('mc_role') === '1' || 
             (activeRole === 'maker' && localStorage.getItem('mc_role') === '3') 
           }
           isfromMail={false}
         />
       )}
    </div>
  );
};

export default MakerChecker;
